import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, TrendingUp, Package, Wallet, AlertTriangle, IndianRupee, Banknote, Smartphone, Landmark, CreditCard, Receipt, X } from "lucide-react";
import { api } from "../lib/api";

const card = "bg-background border border-border p-5";
const field = "w-full border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MODE_LABEL = { cash: "Cash", upi: "UPI", bank: "Bank", card: "Card" };
const MODE_ICON = { cash: Banknote, upi: Smartphone, bank: Landmark, card: CreditCard };
export const ModeBadge = ({ mode }) => {
  const Icon = MODE_ICON[mode] || Banknote;
  return <span className="inline-flex items-center gap-1 text-xs font-body uppercase tracking-wide text-muted-foreground"><Icon className="w-3.5 h-3.5" />{MODE_LABEL[mode] || mode}</span>;
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={card} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between">
      <p className="overline text-muted-foreground">{label}</p>
      <Icon className={`w-4 h-4 ${accent || "text-[hsl(var(--primary))]"}`} strokeWidth={1.5} />
    </div>
    <p className="font-display text-3xl mt-3">{value}</p>
  </div>
);

export const DashboardTab = () => {
  const [d, setD] = useState(null);
  const [rep, setRep] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: `${today.slice(0, 8)}01`, to: today });
  const [preset, setPreset] = useState("month");

  const loadReport = (r) => {
    api.get(`/admin/reports/finance?from=${r.from}&to=${r.to}`).then((x) => setRep(x.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
    loadReport(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    setPreset(p);
    const t = new Date();
    const iso = (dt) => dt.toISOString().slice(0, 10);
    let r;
    if (p === "today") r = { from: iso(t), to: iso(t) };
    else if (p === "7d") r = { from: iso(new Date(Date.now() - 6 * 864e5)), to: iso(t) };
    else if (p === "30d") r = { from: iso(new Date(Date.now() - 29 * 864e5)), to: iso(t) };
    else if (p === "month") r = { from: `${iso(t).slice(0, 8)}01`, to: iso(t) };
    else if (p === "year") r = { from: `${t.getFullYear()}-01-01`, to: iso(t) };
    else r = { from: "", to: "" };
    setRange(r);
    loadReport(r);
  };

  if (!d || !rep) return <p className="font-body text-muted-foreground">Loading…</p>;
  const maxRev = Math.max(1, ...d.daily.map((x) => x.revenue));
  const presets = [["today", "Aaj"], ["7d", "7 din"], ["30d", "30 din"], ["month", "Is mahine"], ["year", "Is saal"], ["all", "Sab"]];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-4xl mb-6">Dashboard</h1>

      {/* Period filter */}
      <div className={`${card} mb-6`} data-testid="finance-filter-bar">
        <p className="overline text-muted-foreground mb-3">Profit-Loss period chunein</p>
        <div className="flex flex-wrap items-center gap-2">
          {presets.map(([k, label]) => (
            <button key={k} onClick={() => applyPreset(k)} data-testid={`preset-${k}`}
              className={`px-4 py-2 text-xs font-body border transition-colors ${preset === k ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border text-foreground/70 hover:border-foreground"}`}>
              {label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" className="border border-border bg-background px-2 py-2 font-body text-xs" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="range-from" />
            <span className="text-muted-foreground text-xs">se</span>
            <input type="date" className="border border-border bg-background px-2 py-2 font-body text-xs" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="range-to" />
            <button onClick={() => { setPreset("custom"); loadReport(range); }} data-testid="apply-range" className="px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs font-body hover:bg-[hsl(var(--secondary))] transition-colors">Apply</button>
          </div>
        </div>
      </div>

      {/* P&L cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={IndianRupee} label="Total Sales" value={inr(rep.total_sales)} />
        <Stat icon={Package} label="Maal ka cost (sold)" value={inr(rep.cogs)} accent="text-[hsl(var(--secondary))]" />
        <Stat icon={TrendingUp} label={`Gross Profit (${rep.gross_profit_percent}%)`} value={inr(rep.gross_profit)} accent={rep.gross_profit >= 0 ? "text-green-600" : "text-[hsl(var(--primary))]"} />
        <Stat icon={Wallet} label="Expenses" value={inr(rep.total_expenses)} accent="text-[hsl(var(--secondary))]" />
        <Stat icon={TrendingUp} label="Net Profit" value={inr(rep.net_profit)} accent={rep.net_profit >= 0 ? "text-green-600" : "text-[hsl(var(--primary))]"} />
      </div>
      <p className="font-body text-xs text-muted-foreground mt-2" data-testid="sales-split">
        Is period mein: {rep.online_orders} online order + {rep.counter_sales} counter sale · Purchases (maal aaya): {inr(rep.purchases_total)} · Net Profit = Gross Profit − Expenses
      </p>

      {/* Cash / UPI / Bank balances */}
      <div className={`${card} mt-6`} data-testid="balance-table">
        <p className="overline text-muted-foreground mb-4">Cash / UPI / Bank — abhi kitna balance hai</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="overline text-muted-foreground border-b border-border">
                <th className="text-left py-2 pr-4">Mode</th>
                <th className="text-right py-2 pr-4">Opening</th>
                <th className="text-right py-2 pr-4">Sale aayi</th>
                <th className="text-right py-2 pr-4">Kharcha</th>
                <th className="text-right py-2 pr-4">Purchase</th>
                <th className="text-right py-2 pr-4">Balance</th>
                <th className="text-right py-2">Bina kharcha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rep.balances.map((b) => {
                const Icon = MODE_ICON[b.mode] || Banknote;
                return (
                  <tr key={b.mode} data-testid={`balance-${b.mode}`}>
                    <td className="py-2.5 pr-4"><span className="inline-flex items-center gap-2 font-600"><Icon className="w-4 h-4 text-[hsl(var(--primary))]" />{MODE_LABEL[b.mode]}</span></td>
                    <td className="text-right py-2.5 pr-4 text-muted-foreground">{inr(b.opening)}</td>
                    <td className="text-right py-2.5 pr-4 text-green-700">+{inr(b.sales_in)}</td>
                    <td className="text-right py-2.5 pr-4 text-[hsl(var(--primary))]">−{inr(b.expense_out)}</td>
                    <td className="text-right py-2.5 pr-4 text-[hsl(var(--primary))]">−{inr(b.purchase_out)}</td>
                    <td className={`text-right py-2.5 pr-4 font-600 ${b.balance >= 0 ? "" : "text-[hsl(var(--primary))]"}`}>{inr(b.balance)}</td>
                    <td className="text-right py-2.5 text-muted-foreground">{inr(b.balance_without_expenses)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="font-body text-xs text-muted-foreground mt-3">Balance = Opening + Sale − Kharcha (expenses) − Purchase payment · "Bina kharcha" mein expenses nahi ghatae gaye. Opening balance Settings se badlein.</p>
      </div>

      {/* Product-wise profit */}
      <div className={`${card} mt-6`} data-testid="product-profit-table">
        <p className="overline text-muted-foreground mb-4">Product-wise profit (purchase rate vs sale rate)</p>
        {rep.products.length === 0 ? <p className="font-body text-sm text-muted-foreground">Is period mein koi sale nahi.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="overline text-muted-foreground border-b border-border">
                  <th className="text-left py-2 pr-4">Product</th>
                  <th className="text-right py-2 pr-4">Qty</th>
                  <th className="text-right py-2 pr-4">Purchase rate</th>
                  <th className="text-right py-2 pr-4">Sale rate</th>
                  <th className="text-right py-2 pr-4">Total sale</th>
                  <th className="text-right py-2 pr-4">Profit</th>
                  <th className="text-right py-2">Profit %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rep.products.map((p) => (
                  <tr key={p.name} data-testid={`profit-row-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <td className="py-2.5 pr-4 font-600">{p.name}</td>
                    <td className="text-right py-2.5 pr-4">{p.qty}</td>
                    <td className="text-right py-2.5 pr-4 text-muted-foreground">{inr(p.purchase_rate)}</td>
                    <td className="text-right py-2.5 pr-4">{inr(p.sale_rate)}</td>
                    <td className="text-right py-2.5 pr-4">{inr(p.sale_total)}</td>
                    <td className={`text-right py-2.5 pr-4 font-600 ${p.profit >= 0 ? "text-green-700" : "text-[hsl(var(--primary))]"}`}>{inr(p.profit)}</td>
                    <td className="text-right py-2.5">{p.profit_percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="font-body text-xs text-muted-foreground mt-3">Purchase rate purchase entry se apne aap update hota hai; product edit karke bhi badal sakte hain.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className={card}>
          <p className="overline text-muted-foreground mb-5">Revenue — last 14 days</p>
          <div className="flex items-end gap-1 h-40">
            {d.daily.map((x) => (
              <div key={x.date} className="flex-1 flex flex-col items-center justify-end group relative" title={`${x.date}: ₹${x.revenue}`}>
                <div className="w-full bg-[hsl(var(--primary))]/80 hover:bg-[hsl(var(--primary))] transition-all" style={{ height: `${(x.revenue / maxRev) * 100}%`, minHeight: x.revenue > 0 ? "3px" : "0" }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between overline text-muted-foreground mt-2">
            <span>{d.daily[0]?.date.slice(5)}</span><span>{d.daily[d.daily.length - 1]?.date.slice(5)}</span>
          </div>
        </div>
        <div className={card}>
          <p className="overline text-muted-foreground mb-5">Best sellers</p>
          {d.best_sellers.length === 0 ? <p className="font-body text-sm text-muted-foreground">No sales yet.</p> : (
            <div className="space-y-3">
              {d.best_sellers.map((b, i) => (
                <div key={b.name} className="flex items-center justify-between">
                  <span className="font-body text-sm"><span className="font-mono-label text-[hsl(var(--primary))] mr-2">#{i + 1}</span>{b.name}</span>
                  <span className="font-body font-600 text-sm">{b.qty} sold</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {d.low_stock.length > 0 && (
        <div className={`${card} mt-6 border-[hsl(var(--primary))]`}>
          <p className="overline text-[hsl(var(--primary))] mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Low stock alert</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.low_stock.map((p) => (
              <div key={p.id} className="border border-border p-3 text-sm font-body">
                <p className="font-600">{p.name}</p>
                <p className="text-muted-foreground text-xs mt-1">{Object.entries(p.low_sizes).map(([s, q]) => `${s}: ${q}`).join(" · ")}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const SalesTab = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const emptyItem = { product_id: "", size: "", qty: 1, price: "" };
  const emptyForm = { customer_name: "", phone: "", payment_mode: "cash", note: "", discount: "", date: today };
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([{ ...emptyItem }]);

  const load = () => api.get("/admin/sales").then((r) => setSales(r.data)).catch(() => {});
  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); loadProducts(); }, []);

  const setItem = (i, patch) => {
    setItems((prev) => prev.map((it, x) => {
      if (x !== i) return it;
      const next = { ...it, ...patch };
      if (patch.product_id !== undefined) {
        const p = products.find((pr) => pr.id === patch.product_id);
        next.price = p ? p.price : "";
        next.size = "";
      }
      return next;
    }));
  };

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0), 0);
  const total = Math.max(0, subtotal - (parseFloat(form.discount) || 0));

  const submit = async () => {
    const valid = items.filter((it) => it.product_id && parseInt(it.qty) > 0);
    if (!valid.length) { toast.error("Kam se kam ek item select karein"); return; }
    try {
      const { data } = await api.post("/admin/sales", {
        ...form,
        discount: parseFloat(form.discount) || 0,
        items: valid.map((it) => ({ product_id: it.product_id, size: it.size, qty: parseInt(it.qty), price: parseFloat(it.price) || 0 })),
      });
      toast.success(`Bill ${data.bill_no} ban gaya — stock update ho gaya`);
      setForm(emptyForm); setItems([{ ...emptyItem }]);
      load(); loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Sale save nahi hui"); }
  };

  const del = async (id) => {
    if (!window.confirm("Ye sale delete karein? Stock wapas add ho jayega.")) return;
    await api.delete(`/admin/sales/${id}`); load(); loadProducts();
  };

  return (
    <div data-testid="admin-sales">
      <h1 className="font-display text-4xl mb-8">Sales / Counter Billing</h1>

      <div className={card}>
        <p className="overline text-muted-foreground mb-4">Nayi sale (dukaan / counter)</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input className={field} placeholder="Customer name (optional)" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="sale-customer-name" />
          <input className={field} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sale-phone" />
          <select className={field} value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="sale-payment-mode">
            <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="card">Card</option>
          </select>
          <input type="date" className={field} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="sale-date" />
        </div>

        <div className="mt-4 space-y-3">
          {items.map((it, i) => {
            const p = products.find((pr) => pr.id === it.product_id);
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sale-item-row-${i}`}>
                <select className={`${field} col-span-12 sm:col-span-5`} value={it.product_id} onChange={(e) => setItem(i, { product_id: e.target.value })} data-testid={`sale-item-product-${i}`}>
                  <option value="">Product select karein…</option>
                  {products.map((pr) => <option key={pr.id} value={pr.id}>{pr.name} — ₹{pr.price}</option>)}
                </select>
                <select className={`${field} col-span-4 sm:col-span-2`} value={it.size} onChange={(e) => setItem(i, { size: e.target.value })} disabled={!p} data-testid={`sale-item-size-${i}`}>
                  <option value="">Size…</option>
                  {(p?.sizes || []).map((s) => <option key={s} value={s}>{s} ({p?.stock?.[s] ?? 0})</option>)}
                </select>
                <input className={`${field} col-span-3 sm:col-span-2`} type="number" min="1" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid={`sale-item-qty-${i}`} />
                <input className={`${field} col-span-4 sm:col-span-2`} type="number" min="0" placeholder="Rate ₹" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} data-testid={`sale-item-price-${i}`} />
                <button onClick={() => setItems((prev) => prev.filter((_, x) => x !== i))} className="col-span-1 text-[hsl(var(--primary))] justify-self-center" data-testid={`sale-item-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button onClick={() => setItems((prev) => [...prev, { ...emptyItem }])} data-testid="sale-add-item" className="inline-flex items-center gap-2 border border-border px-4 py-2 font-body text-xs hover:border-foreground transition-colors"><Plus className="w-3.5 h-3.5" /> Item add karein</button>
          <input className={`${field} w-36`} type="number" min="0" placeholder="Discount ₹" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="sale-discount" />
          <input className={`${field} flex-1 min-w-[180px]`} placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>

        <div className="flex items-center justify-between mt-5 border-t border-border pt-4">
          <p className="font-body text-sm" data-testid="sale-total-preview">Total: <span className="font-display text-2xl">{inr(total)}</span> {subtotal !== total && <span className="text-muted-foreground text-xs">(subtotal {inr(subtotal)})</span>}</p>
          <button onClick={submit} data-testid="sale-submit" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors"><Receipt className="w-4 h-4" /> Sale save karein</button>
        </div>
      </div>

      <div className={`${card} mt-6`}>
        <p className="overline text-muted-foreground mb-4">Recent counter sales ({sales.length})</p>
        {sales.length === 0 ? <p className="font-body text-sm text-muted-foreground">Abhi tak koi counter sale nahi.</p> : (
          <div className="divide-y divide-border">
            {sales.map((s) => (
              <div key={s.id} className="py-3 flex flex-wrap items-center gap-3 text-sm font-body" data-testid={`sale-${s.id}`}>
                <span className="font-mono-label text-[hsl(var(--primary))] text-xs">{s.bill_no}</span>
                <span className="text-muted-foreground text-xs">{s.date}</span>
                <span className="font-600">{s.customer_name || "Walk-in"}</span>
                <span className="text-muted-foreground text-xs flex-1 min-w-[140px]">{(s.items || []).map((i) => `${i.name}${i.size ? ` (${i.size})` : ""} ×${i.qty}`).join(", ")}</span>
                <ModeBadge mode={s.payment_mode} />
                <span className="font-600">{inr(s.total)}</span>
                <span className={`text-xs ${s.profit >= 0 ? "text-green-700" : "text-[hsl(var(--primary))]"}`}>profit {inr(s.profit)}</span>
                <button onClick={() => del(s.id)} className="text-[hsl(var(--primary))]" data-testid={`del-sale-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const InventoryTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sup, setSup] = useState({ name: "", phone: "", notes: "" });
  const [po, setPo] = useState({ product_id: "", supplier_id: "", size: "", qty: "", cost_per_unit: "", payment_mode: "cash" });

  const loadSup = () => api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  const loadPur = () => api.get("/admin/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  useEffect(() => { loadSup(); loadPur(); api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const addSup = async () => {
    if (!sup.name.trim()) { toast.error("Supplier name required"); return; }
    await api.post("/admin/suppliers", sup); toast.success("Supplier added"); setSup({ name: "", phone: "", notes: "" }); loadSup();
  };
  const delSup = async (id) => { if (!window.confirm("Delete supplier?")) return; await api.delete(`/admin/suppliers/${id}`); loadSup(); };

  const selProduct = products.find((p) => p.id === po.product_id);
  const addPurchase = async () => {
    if (!po.product_id || !po.size || !po.qty) { toast.error("Product, size & qty required"); return; }
    const s = suppliers.find((x) => x.id === po.supplier_id);
    await api.post("/admin/purchases", {
      product_id: po.product_id, supplier_id: po.supplier_id || null, supplier_name: s ? s.name : "",
      size: po.size, qty: parseInt(po.qty), cost_per_unit: parseFloat(po.cost_per_unit) || 0, payment_mode: po.payment_mode,
    });
    toast.success("Stock added — inventory updated"); setPo({ product_id: "", supplier_id: "", size: "", qty: "", cost_per_unit: "", payment_mode: "cash" });
    loadPur(); api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };

  return (
    <div data-testid="admin-inventory">
      <h1 className="font-display text-4xl mb-8">Inventory & Purchase</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className={card}>
          <p className="overline text-muted-foreground mb-4">Add stock (purchase / maal aaya)</p>
          <div className="space-y-3">
            <select className={field} value={po.product_id} onChange={(e) => setPo({ ...po, product_id: e.target.value, size: "" })} data-testid="po-product">
              <option value="">Select product…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select className={field} value={po.size} onChange={(e) => setPo({ ...po, size: e.target.value })} data-testid="po-size" disabled={!selProduct}>
                <option value="">Size…</option>
                {(selProduct?.sizes || []).map((s) => <option key={s} value={s}>{s} (stock: {selProduct?.stock?.[s] ?? 0})</option>)}
              </select>
              <input className={field} type="number" placeholder="Qty" value={po.qty} onChange={(e) => setPo({ ...po, qty: e.target.value })} data-testid="po-qty" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={field} type="number" placeholder="Cost per unit ₹ (purchase rate)" value={po.cost_per_unit} onChange={(e) => setPo({ ...po, cost_per_unit: e.target.value })} data-testid="po-cost" />
              <select className={field} value={po.supplier_id} onChange={(e) => setPo({ ...po, supplier_id: e.target.value })} data-testid="po-supplier">
                <option value="">Supplier (optional)…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <select className={field} value={po.payment_mode} onChange={(e) => setPo({ ...po, payment_mode: e.target.value })} data-testid="po-payment-mode">
              <option value="cash">Payment: Cash</option><option value="upi">Payment: UPI</option><option value="bank">Payment: Bank</option><option value="card">Payment: Card</option>
            </select>
            <button onClick={addPurchase} data-testid="add-purchase-btn" className="w-full bg-[hsl(var(--primary))] text-white py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add to stock</button>
          </div>
        </div>

        <div className={card}>
          <p className="overline text-muted-foreground mb-4">Suppliers (vendors)</p>
          <div className="space-y-3">
            <input className={field} placeholder="Supplier name" value={sup.name} onChange={(e) => setSup({ ...sup, name: e.target.value })} data-testid="sup-name" />
            <div className="grid grid-cols-2 gap-3">
              <input className={field} placeholder="Phone" value={sup.phone} onChange={(e) => setSup({ ...sup, phone: e.target.value })} data-testid="sup-phone" />
              <input className={field} placeholder="Notes" value={sup.notes} onChange={(e) => setSup({ ...sup, notes: e.target.value })} />
            </div>
            <button onClick={addSup} data-testid="add-supplier-btn" className="w-full border border-border py-2.5 font-body text-sm hover:border-foreground transition-colors flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add supplier</button>
          </div>
          <div className="mt-4 divide-y divide-border">
            {suppliers.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 text-sm font-body" data-testid={`supplier-${s.id}`}>
                <span>{s.name} <span className="text-muted-foreground">{s.phone}</span></span>
                <button onClick={() => delSup(s.id)} className="text-[hsl(var(--primary))]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${card} mt-6`}>
        <p className="overline text-muted-foreground mb-4">Recent purchases</p>
        {purchases.length === 0 ? <p className="font-body text-sm text-muted-foreground">No purchases yet.</p> : (
          <div className="divide-y divide-border">
            {purchases.map((p) => (
              <div key={p.id} className="flex justify-between py-2 text-sm font-body items-center" data-testid={`purchase-${p.id}`}>
                <span>{p.product_name} · {p.size} × {p.qty} {p.supplier_name && <span className="text-muted-foreground">from {p.supplier_name}</span>} <ModeBadge mode={p.payment_mode || "cash"} /></span>
                <span className="flex items-center gap-3"><span className="font-600">₹{p.total_cost}</span>
                  <button onClick={async () => { if (window.confirm("Delete this purchase record?")) { await api.delete(`/admin/purchases/${p.id}`); loadPur(); } }} className="text-[hsl(var(--primary))]" data-testid={`del-purchase-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ExpensesTab = () => {
  const [expenses, setExpenses] = useState([]);
  const [ex, setEx] = useState({ category: "", amount: "", note: "", payment_mode: "cash" });
  const load = () => api.get("/admin/expenses").then((r) => setExpenses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const add = async () => {
    if (!ex.category.trim() || !ex.amount) { toast.error("Category & amount required"); return; }
    await api.post("/admin/expenses", { ...ex, amount: parseFloat(ex.amount) }); toast.success("Expense added"); setEx({ category: "", amount: "", note: "", payment_mode: "cash" }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete expense?")) return; await api.delete(`/admin/expenses/${id}`); load(); };
  return (
    <div data-testid="admin-expenses" className="max-w-3xl">
      <h1 className="font-display text-4xl mb-8">Expenses</h1>
      <div className={card}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input className={field} placeholder="Category (rent, salary…)" value={ex.category} onChange={(e) => setEx({ ...ex, category: e.target.value })} data-testid="ex-category" />
          <input className={field} type="number" placeholder="Amount ₹" value={ex.amount} onChange={(e) => setEx({ ...ex, amount: e.target.value })} data-testid="ex-amount" />
          <select className={field} value={ex.payment_mode} onChange={(e) => setEx({ ...ex, payment_mode: e.target.value })} data-testid="ex-payment-mode">
            <option value="cash">Cash se</option><option value="upi">UPI se</option><option value="bank">Bank se</option><option value="card">Card se</option>
          </select>
          <input className={field} placeholder="Note (optional)" value={ex.note} onChange={(e) => setEx({ ...ex, note: e.target.value })} />
        </div>
        <button onClick={add} data-testid="add-expense-btn" className="mt-3 bg-[hsl(var(--primary))] text-white px-6 py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors flex items-center gap-2"><Plus className="w-4 h-4" /> Add expense</button>
      </div>
      <div className="flex justify-between items-center mt-6 mb-3">
        <p className="overline text-muted-foreground">Total expenses</p>
        <p className="font-display text-2xl text-[hsl(var(--primary))]" data-testid="total-expenses">₹{total.toFixed(0)}</p>
      </div>
      <div className="border border-border divide-y divide-border">
        {expenses.map((e) => (
          <div key={e.id} className="flex justify-between items-center p-4 text-sm font-body" data-testid={`expense-${e.id}`}>
            <div><span className="font-600 capitalize">{e.category}</span> <span className="text-muted-foreground">· {e.date} {e.note && `· ${e.note}`}</span> <ModeBadge mode={e.payment_mode || "cash"} /></div>
            <div className="flex items-center gap-4"><span className="font-600">₹{e.amount}</span><button onClick={() => del(e.id)} className="text-[hsl(var(--primary))]"><Trash2 className="w-4 h-4" /></button></div>
          </div>
        ))}
        {expenses.length === 0 && <p className="p-4 font-body text-sm text-muted-foreground">No expenses yet.</p>}
      </div>
    </div>
  );
};

export const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-customers">
      <h1 className="font-display text-4xl mb-8">Customers ({customers.length})</h1>
      {customers.length === 0 ? <p className="font-body text-muted-foreground">No customers yet.</p> : (
        <div className="border border-border divide-y divide-border">
          <div className="grid grid-cols-12 gap-2 p-3 overline text-muted-foreground bg-card">
            <span className="col-span-4">Customer</span><span className="col-span-4">Contact</span><span className="col-span-2 text-center">Orders</span><span className="col-span-2 text-right">Spent</span>
          </div>
          {customers.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 p-3 text-sm font-body items-center" data-testid={`customer-${i}`}>
              <span className="col-span-4 font-600">{c.name || "—"}</span>
              <span className="col-span-4 text-muted-foreground text-xs">{c.phone}{c.city ? ` · ${c.city}` : ""}</span>
              <span className="col-span-2 text-center">{c.orders}</span>
              <span className="col-span-2 text-right font-600">₹{c.total_spent}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export function printInvoice(order, store) {
  const gst = Number(store?.gst_percent || 0);
  const grand = Number(order.total || 0);
  const taxAmt = gst > 0 ? +(grand - grand / (1 + gst / 100)).toFixed(2) : 0;
  const rows = (order.items || []).map((i) => `<tr><td style="padding:6px;border-bottom:1px solid #eee">${i.name}${i.size ? " / " + i.size : ""}</td><td style="padding:6px;border-bottom:1px solid #eee;text-align:center">${i.qty}</td><td style="padding:6px;border-bottom:1px solid #eee;text-align:right">₹${i.price}</td><td style="padding:6px;border-bottom:1px solid #eee;text-align:right">₹${i.price * i.qty}</td></tr>`).join("");
  const html = `<!doctype html><html><head><title>Invoice ${order.tracking_id || ""}</title><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;color:#1a1a1a;max-width:720px;margin:24px auto;padding:0 16px}h1{color:#9C3D32;margin:0}table{width:100%;border-collapse:collapse;margin-top:16px}th{text-align:left;border-bottom:2px solid #1a1a1a;padding:6px;font-size:12px}@media print{button{display:none}}</style></head><body>
  <div style="display:flex;justify-content:space-between;align-items:center"><div><h1>Navrang Udaipur</h1><p style="margin:4px 0;color:#666;font-size:13px">${store?.address || "Udaipur, Rajasthan"}</p>${store?.gstin ? `<p style="margin:0;color:#666;font-size:12px">GSTIN: ${store.gstin}</p>` : ""}</div><div style="text-align:right"><h2 style="margin:0">INVOICE</h2><p style="margin:4px 0;font-size:13px">#${order.tracking_id || ""}</p><p style="margin:0;font-size:12px;color:#666">${(order.created_at || "").slice(0, 10)}</p></div></div>
  <div style="margin-top:16px;font-size:13px"><strong>Bill to:</strong> ${order.customer_name || ""}<br>${order.phone || ""}<br>${order.address || ""}, ${order.city || ""} - ${order.pincode || ""}</div>
  <table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
  <div style="margin-top:16px;margin-left:auto;width:260px;font-size:14px">
  ${order.subtotal != null ? `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>Subtotal</span><span>₹${order.subtotal}</span></div>` : ""}
  ${order.discount ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#9C3D32"><span>Discount${order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>-₹${order.discount}</span></div>` : ""}
  ${gst > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#666"><span>GST ${gst}% (incl.)</span><span>₹${taxAmt}</span></div>` : ""}
  <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #1a1a1a;font-weight:bold;font-size:16px"><span>Grand Total</span><span>₹${grand}</span></div>
  <div style="font-size:12px;color:#666;margin-top:4px">Payment: ${order.payment_method || ""} · ${order.status || ""}</div></div>
  <p style="margin-top:28px;font-size:12px;color:#999;text-align:center">Thank you for shopping with Navrang Udaipur — hand block-printed Sanganeri shirts.</p>
  <div style="text-align:center;margin-top:16px"><button onclick="window.print()" style="background:#9C3D32;color:#fff;border:0;padding:10px 24px;cursor:pointer">Print / Save PDF</button></div>
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}
